import pluralize from '../_utils/tools/pluralize';

const itemUnavailable = (timestamp) =>
  timestamp && !timestamp.service_available;

// When an order has multiple items within one delivery option, the customer should be told to
// collect at the "latest" time, to ensure everything is ready for them.
// "latest" is not within timestamps for a single item, but the item that will take the longest.
const latestPickupAvailable = (currentTime, currentLatestPickup) => {
  if (!currentLatestPickup) {
    return currentTime;
  }
  return currentTime.getTime() > currentLatestPickup.getTime()
    ? currentTime
    : currentLatestPickup;
};

const PICKUP_TYPE = {
  RA: '1-hour pickup', // old, safe to remove
  PHOTO_ONE_HOUR_PICKUP: '1-hour pickup',
  RA1: 'Same-day pickup', // old, safe to remove
  PHOTO_SAME_DAY_PICKUP: 'Same-day pickup',
};

const unavailableOrderItems = (prismItems, locations) => {
  return locations.reduce((unavailableItems, location) => {
    const locationOrderTimestamps = location.orderTimestamps;

    if (!locationOrderTimestamps) {
      return unavailableItems;
    }

    Object.entries(locationOrderTimestamps).forEach(
      ([deliveryType, itemsWithTimestamp]) => {
        if (itemUnavailable(itemsWithTimestamp.prints)) {
          unavailableItems.push({
            prints: true,
            storeId: location.id,
            message: {
              full: `Prints are unavailable for ${PICKUP_TYPE[deliveryType]} from your chosen store.`,
              short: 'Prints are unavailable',
            },
          });
        }

        prismItems.forEach((item) => {
          const timestamp = itemsWithTimestamp[item.id];
          if (itemUnavailable(timestamp)) {
            unavailableItems.push({
              prismId: item.id,
              storeId: location.id,
              message: {
                full: `${pluralize(
                  item.product_title,
                  item.quantity,
                )} are unavailable for ${
                  PICKUP_TYPE[deliveryType]
                } from your chosen store.`,
                short: `${item.product_title} is unavailable`,
              },
            });
          }
        });
      },
    );

    return unavailableItems;
  }, []);
};

const availableOrderItems = (prismItems, locations) => {
  return locations.flatMap((location) => {
    const { orderTimestamps, id: storeId } = location;
    if (!orderTimestamps) return [];

    return Object.entries(orderTimestamps).flatMap(
      ([deliveryType, itemsWithTimestamp]) =>
        prismItems
          .filter((item) => !itemUnavailable(itemsWithTimestamp[item.id]))
          .map((item) => {
            const timestamp = itemsWithTimestamp[item.id];
            const pickupDate = timestamp?.pickup_time
              ? new Date(timestamp.pickup_time)
              : '';
            return {
              prismId: item.id,
              storeId,
              message: {
                full: `${pluralize(item.product_title, item.quantity)} are available for ${
                  PICKUP_TYPE[deliveryType]
                } from your chosen store at ${pickupDate} hours.`,
                short: `${item.product_title} is available`,
              },
            };
          }),
    );
  });
};

const availablePickupTimes = (orderTimestamps = {}) => {
  return Object.entries(orderTimestamps).reduce(
    (pickupTimes, [delivery_type, timestamp]) => {
      let latestPickup;

      Object.values(timestamp).forEach((currentTimestamp) => {
        if (itemUnavailable(currentTimestamp)) {
          return;
        }

        latestPickup = latestPickupAvailable(
          new Date(currentTimestamp.pickup_time),
          latestPickup,
        );
      });

      if (latestPickup) {
        pickupTimes.push(
          `${
            PICKUP_TYPE[delivery_type]
          } available ${latestPickup.toLocaleString()}`,
        );
      }

      return pickupTimes;
    },
    [],
  );
};

const availablePickupTimesShortFormat = (orderTimestamps = {}) => {
  // default options for toLocaleString
  // no need to show date if the timestamp is today
  const today = new Date( Date.now() ); // used for comparison only
  const formatOptions = (datetime, today) => {
    // default: "3:00 PM"
    let options = {
      hour: 'numeric',
      minute: 'numeric',
      hour12: true,
    }

    // different date in same year: "Apr 21st 3:00 PM"
    if (
      (datetime.getDate() !== today.getDate() || datetime.getMonth() !== datetime.getMonth())
    ) {
      options.month = 'short';
      options.day = 'numeric';
    }

    // different year: "Apr 21st, 2023 3:00 PM"
    if (datetime.getFullYear() !== today.getFullYear()) {
      options.month = 'short';
      options.day = 'numeric';
      options.year = 'numeric';
    }

    return options;
  }

  return Object.entries(orderTimestamps).reduce(
    (pickupTimes, [_delivery_type, timestamp]) => {
      let latestPickup;

      Object.values(timestamp).forEach((currentTimestamp) => {
        if (itemUnavailable(currentTimestamp)) {
          return;
        }

        latestPickup = latestPickupAvailable(
          new Date(currentTimestamp.pickup_time),
          latestPickup,
        );
      });

      if (latestPickup) {
        pickupTimes.push(
          latestPickup.toLocaleString('en-US', formatOptions(latestPickup, today)),
        );
      }

      return pickupTimes;
    },
    [],
  );
};

const orderTimestampsPresent = (locations = {}) =>
  Object.values(locations).some(
    (deliveryKey) =>
      deliveryKey &&
      [...deliveryKey].find((delivery) => delivery?.orderTimestamps),
  );

export {
  unavailableOrderItems,
  availablePickupTimes,
  availablePickupTimesShortFormat,
  orderTimestampsPresent,
  availableOrderItems
};
